/**
 * Pitch Detection for BRR Samples
 *
 * Uses FFT with Harmonic Product Spectrum (HPS) to estimate the fundamental
 * frequency of a sample, then converts to the nearest MIDI note.
 */

import { pitchToString } from './editor/model/NoteParser.js';

// =============================================================================
// FFT Implementation (Radix-2 Cooley-Tukey)
// =============================================================================

/**
 * Compute FFT of real-valued input.
 * Returns complex array [real0, imag0, real1, imag1, ...]
 */
function fft(input: Float32Array): Float32Array {
  const n = input.length;
  if (n & (n - 1)) {
    throw new Error('FFT size must be a power of 2');
  }

  // Copy to complex array
  const data = new Float32Array(n * 2);
  for (let i = 0; i < n; i++) {
    data[i * 2] = input[i];
    data[i * 2 + 1] = 0;
  }

  // Bit reversal permutation
  const bits = Math.log2(n);
  for (let i = 0; i < n; i++) {
    const j = reverseBits(i, bits);
    if (j > i) {
      // Swap complex values
      const tr = data[i * 2];
      const ti = data[i * 2 + 1];
      data[i * 2] = data[j * 2];
      data[i * 2 + 1] = data[j * 2 + 1];
      data[j * 2] = tr;
      data[j * 2 + 1] = ti;
    }
  }

  // Cooley-Tukey FFT
  for (let size = 2; size <= n; size *= 2) {
    const halfSize = size / 2;
    const step = (2 * Math.PI) / size;

    for (let i = 0; i < n; i += size) {
      for (let j = 0; j < halfSize; j++) {
        const angle = -step * j;
        const wr = Math.cos(angle);
        const wi = Math.sin(angle);

        const evenIdx = (i + j) * 2;
        const oddIdx = (i + j + halfSize) * 2;

        const er = data[evenIdx];
        const ei = data[evenIdx + 1];
        const or = data[oddIdx];
        const oi = data[oddIdx + 1];

        // Complex multiplication: (or + oi*i) * (wr + wi*i)
        const tr = or * wr - oi * wi;
        const ti = or * wi + oi * wr;

        data[evenIdx] = er + tr;
        data[evenIdx + 1] = ei + ti;
        data[oddIdx] = er - tr;
        data[oddIdx + 1] = ei - ti;
      }
    }
  }

  return data;
}

function reverseBits(x: number, bits: number): number {
  let result = 0;
  for (let i = 0; i < bits; i++) {
    result = (result << 1) | (x & 1);
    x >>= 1;
  }
  return result;
}

/**
 * Compute magnitude spectrum from complex FFT output.
 * Returns only the first half (positive frequencies).
 */
function magnitudeSpectrum(fftData: Float32Array): Float32Array {
  const n = fftData.length / 2;
  const halfN = n / 2;
  const magnitudes = new Float32Array(halfN);

  for (let i = 0; i < halfN; i++) {
    const real = fftData[i * 2];
    const imag = fftData[i * 2 + 1];
    magnitudes[i] = Math.sqrt(real * real + imag * imag);
  }

  return magnitudes;
}

// =============================================================================
// Pitch Detection
// =============================================================================

export interface PitchDetectionResult {
  /** Estimated frequency in Hz */
  frequency: number;
  /** MIDI note number (fractional) */
  midiNote: number;
  /** Nearest integer MIDI note */
  nearestNote: number;
  /** Note name (e.g., "C4", "F#3") */
  noteName: string;
  /** Cents deviation from nearest note (-50 to +50) */
  cents: number;
  /** Confidence score 0-1 (higher = more confident) */
  confidence: number;
}

export interface PitchDetectionOptions {
  /** Sample rate of the input (default: 32000 for SNES) */
  sampleRate?: number;
  /** FFT size (default: 4096, must be power of 2) */
  fftSize?: number;
  /** Number of harmonics to use for HPS (default: 5) */
  hpsHarmonics?: number;
  /** Minimum frequency to consider in Hz (default: 20) */
  minFrequency?: number;
  /** Maximum frequency to consider in Hz (default: 4000) */
  maxFrequency?: number;
}

/**
 * Convert frequency to MIDI note number.
 * A4 = 440Hz = MIDI note 69
 */
export function frequencyToMidi(frequency: number): number {
  return 69 + 12 * Math.log2(frequency / 440);
}

/**
 * Convert MIDI note number to frequency.
 */
export function midiToFrequency(midiNote: number): number {
  return 440 * Math.pow(2, (midiNote - 69) / 12);
}

// Re-export pitchToString as midiToNoteName for backwards compatibility
export { pitchToString as midiToNoteName } from './editor/model/NoteParser.js';

/**
 * Apply Hann window to reduce spectral leakage.
 */
function applyHannWindow(samples: Float32Array): Float32Array {
  const n = samples.length;
  const windowed = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const window = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
    windowed[i] = samples[i] * window;
  }
  return windowed;
}

/**
 * Harmonic Product Spectrum - multiply spectrum by downsampled versions
 * to emphasize the fundamental frequency.
 */
function harmonicProductSpectrum(magnitudes: Float32Array, numHarmonics: number): Float32Array {
  const n = magnitudes.length;
  const hps = new Float32Array(Math.floor(n / numHarmonics));

  for (let i = 0; i < hps.length; i++) {
    let product = magnitudes[i];
    for (let h = 2; h <= numHarmonics; h++) {
      const idx = i * h;
      if (idx < n) {
        product *= magnitudes[idx];
      }
    }
    hps[i] = product;
  }

  return hps;
}

/**
 * Find peak with parabolic interpolation for sub-bin accuracy.
 */
function findPeakWithInterpolation(
  spectrum: Float32Array,
  minBin: number,
  maxBin: number
): { bin: number; magnitude: number } {
  // Find max bin in range
  let maxMag = -Infinity;
  let maxBinIdx = minBin;

  for (let i = minBin; i < maxBin && i < spectrum.length; i++) {
    if (spectrum[i] > maxMag) {
      maxMag = spectrum[i];
      maxBinIdx = i;
    }
  }

  // Parabolic interpolation if we have neighbors
  if (maxBinIdx > 0 && maxBinIdx < spectrum.length - 1) {
    const alpha = spectrum[maxBinIdx - 1];
    const beta = spectrum[maxBinIdx];
    const gamma = spectrum[maxBinIdx + 1];

    // Peak offset from maxBinIdx
    const p = (0.5 * (alpha - gamma)) / (alpha - 2 * beta + gamma);

    return {
      bin: maxBinIdx + p,
      magnitude: beta - 0.25 * (alpha - gamma) * p,
    };
  }

  return { bin: maxBinIdx, magnitude: maxMag };
}

/**
 * Check if a signal has harmonics at the given fundamental frequency.
 * Returns a score 0-1 indicating harmonic presence.
 */
function hasHarmonics(
  magnitudes: Float32Array,
  fundamentalBin: number,
  numHarmonics: number
): number {
  const fundMag = magnitudes[Math.round(fundamentalBin)];
  if (fundMag === 0) return 0;

  let harmonicSum = 0;
  let harmonicCount = 0;

  for (let h = 2; h <= numHarmonics; h++) {
    const harmonicBin = Math.round(fundamentalBin * h);
    if (harmonicBin < magnitudes.length) {
      // Check a small window around the expected harmonic
      let maxInWindow = 0;
      for (let offset = -2; offset <= 2; offset++) {
        const idx = harmonicBin + offset;
        if (idx >= 0 && idx < magnitudes.length) {
          maxInWindow = Math.max(maxInWindow, magnitudes[idx]);
        }
      }
      harmonicSum += maxInWindow / fundMag;
      harmonicCount++;
    }
  }

  // Return average harmonic strength relative to fundamental
  return harmonicCount > 0 ? harmonicSum / harmonicCount : 0;
}

/**
 * Detect the pitch of a sample using FFT with optional HPS refinement.
 * Uses raw FFT for pure tones, HPS for complex tones with harmonics.
 */
export function detectPitch(
  samples: Float32Array | Int16Array,
  options: PitchDetectionOptions = {}
): PitchDetectionResult | null {
  const {
    sampleRate = 32000,
    fftSize = 4096,
    hpsHarmonics = 5,
    minFrequency = 20,
    maxFrequency = 4000,
  } = options;

  // Convert Int16 to Float32 if needed
  let floatSamples: Float32Array;
  if (samples instanceof Int16Array) {
    floatSamples = new Float32Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
      floatSamples[i] = samples[i] / 32768;
    }
  } else {
    floatSamples = samples;
  }

  // Need at least fftSize samples
  if (floatSamples.length < fftSize) {
    // Pad with zeros if too short
    const padded = new Float32Array(fftSize);
    padded.set(floatSamples);
    floatSamples = padded;
  }

  // Use middle section of sample (often more stable pitch)
  const startOffset = Math.max(0, Math.floor((floatSamples.length - fftSize) / 2));
  const segment = floatSamples.slice(startOffset, startOffset + fftSize);

  // Apply window function
  const windowed = applyHannWindow(segment);

  // Compute FFT and magnitude spectrum
  const fftData = fft(windowed);
  const magnitudes = magnitudeSpectrum(fftData);

  // Convert frequency bounds to bins
  const binResolution = sampleRate / fftSize;
  const minBin = Math.max(1, Math.ceil(minFrequency / binResolution));
  const maxBinRaw = Math.min(magnitudes.length - 1, Math.floor(maxFrequency / binResolution));

  // Find peak in raw magnitude spectrum first
  const rawPeak = findPeakWithInterpolation(magnitudes, minBin, maxBinRaw);
  const rawFrequency = rawPeak.bin * binResolution;

  // Check if the signal has harmonics (complex tone vs pure tone)
  const harmonicScore = hasHarmonics(magnitudes, rawPeak.bin, hpsHarmonics);

  let frequency: number;
  let peakMagnitude: number;

  if (harmonicScore > 0.1) {
    // Complex tone with harmonics - use HPS to find fundamental
    const hps = harmonicProductSpectrum(magnitudes, hpsHarmonics);
    const maxBinHps = Math.min(hps.length - 1, Math.floor(maxFrequency / binResolution));
    const hpsPeak = findPeakWithInterpolation(hps, minBin, maxBinHps);
    const hpsFrequency = hpsPeak.bin * binResolution;

    // Use HPS result only if it's reasonable (within 2 octaves of raw peak)
    const freqRatio = rawFrequency / hpsFrequency;
    if (freqRatio >= 0.25 && freqRatio <= 4) {
      frequency = hpsFrequency;
      peakMagnitude = hpsPeak.magnitude;
    } else {
      frequency = rawFrequency;
      peakMagnitude = rawPeak.magnitude;
    }
  } else {
    // Pure tone - use raw FFT peak
    frequency = rawFrequency;
    peakMagnitude = rawPeak.magnitude;
  }

  // Check if we have a valid result
  if (frequency < minFrequency || frequency > maxFrequency || !isFinite(frequency)) {
    return null;
  }

  // Compute confidence based on peak prominence
  const avgMagnitude = magnitudes.reduce((a, b) => a + b, 0) / magnitudes.length;
  const confidence = Math.min(1, peakMagnitude / (avgMagnitude * 10));

  // Convert to MIDI
  const midiNote = frequencyToMidi(frequency);
  const nearestNote = Math.round(midiNote);
  const cents = (midiNote - nearestNote) * 100;
  const noteName = pitchToString(nearestNote);

  return {
    frequency,
    midiNote,
    nearestNote,
    noteName,
    cents,
    confidence,
  };
}

/**
 * Estimate the base note for a BRR sample.
 * Returns the nearest MIDI note number.
 */
export function estimateBaseNote(
  samples: Float32Array | Int16Array,
  sampleRate: number = 32000
): number {
  const result = detectPitch(samples, { sampleRate });
  if (result && result.confidence > 0.1) {
    return result.nearestNote;
  }
  // Default to middle C if detection fails
  return 60;
}
